/**
 * Pure geometry for the level-up XP bar choreography (specs/xp-live-feedback Story B).
 * The bar animates from `from` up to 1.0 (completing the old level), resets to 0, then
 * fills to `end` (the overflow remainder carried into the new level). Kept pure so the
 * fill math is unit-testable without running the animation.
 */

import { LEVEL_UP_BAR } from '../../constants/celebrations.constants';

export interface LevelUpBarPlan {
  /** 0..1 fill the bar starts from before climbing to full. */
  from: number;
  /** 0..1 fill the bar lands on after the reset (progress into the new level). */
  end: number;
}

const clamp01 = (n: number): number => {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
};

export interface LevelUpBarInput {
  /** XP earned into the new level. */
  xpCurrent: number;
  /** XP required to complete the new level (≤ 0 at max level). */
  xpTarget: number;
  /** Optional pre-earn fill in the old level (0..1). */
  startProgress?: number;
}

export function getLevelUpBarPlan({
  xpCurrent,
  xpTarget,
  startProgress,
}: LevelUpBarInput): LevelUpBarPlan {
  const from = clamp01(startProgress ?? LEVEL_UP_BAR.DEFAULT_START);
  // Max level (no "next level" target) reads as a full, completed bar.
  const end = xpTarget > 0 ? clamp01(xpCurrent / xpTarget) : 1;
  return { from, end };
}
